import type { FieldPath, FieldValues } from 'react-hook-form'
import type { Style } from 'inlines'
import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import type {
  ComponentProps,
  FocusEventHandler,
  KeyboardEventHandler,
  MutableRefObject,
  ReactNode,
} from 'react'
import { TextInput } from '../../input/text/TextInput'
import { FormField } from '../FormField'
import { useFieldRequired } from '../formSchemaContext'

export type TextFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  label?: string
  labelStyle?: Style
  tooltipText?: string
  required?: boolean
  placeholder?: string
  disabled?: boolean
  icon?: IconDefinition
  endAdornment?: ReactNode
  style?: Style
  containerStyle?: Style
  inputRef?: MutableRefObject<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  inputType?: ComponentProps<'input'>['type']
  inputMode?: ComponentProps<'input'>['inputMode']
  maxLength?: number
}

export const TextField = <T extends FieldValues = FieldValues>({
  name,
  inputRef,
  required,
  ...inputProps
}: TextFieldProps<T>) => {
  const fieldRequired = useFieldRequired(name)
  const isRequired = required ?? fieldRequired

  return (
    <FormField<T>
      name={name}
      render={({ value, onChange, onBlur, errorMessage }) => (
        <TextInput
          {...inputProps}
          required={isRequired}
          text={(value as string) ?? ''}
          setText={onChange}
          onBlur={onBlur}
          errorMessage={errorMessage}
          ref={inputRef}
        />
      )}
    />
  )
}
